import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, CheckCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

const timeAgo = (date) => {
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat('ar', { numeric: 'auto' });
  if (diff < 60) return 'الآن';
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
};

/**
 * جرس إشعارات العميل (تغيير حالة الطلب). بيسأل السيرفر كل 30 ثانية
 * ولو وصل إشعار جديد بيظهر تنبيه منبثق.
 */
const NotificationBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const knownIds = useRef(null); // أول تحميل: القديم مش "جديد"

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data?.data || { items: [], unread: 0 };
      setItems(data.items);
      setUnread(data.unread);

      if (knownIds.current) {
        data.items
          .filter((n) => !n.read && !knownIds.current.has(n.id))
          .slice(0, 3)
          .forEach((n) =>
            toast(`${n.emoji} ${n.title}`, {
              description: n.order_number ? `طلب ${n.order_number} — ${n.body}` : n.body,
              duration: 8000,
              action: { label: 'طلباتي', onClick: () => navigate('/account?tab=orders') },
            })
          );
      }
      knownIds.current = new Set(data.items.map((n) => n.id));
    } catch {
      /* مؤقتاً مش متاح — المحاولة الجاية */
    }
  }, [token, navigate]);

  useEffect(() => {
    knownIds.current = null;
    load();
    const timer = setInterval(() => !document.hidden && load(), POLL_MS);
    const onVisible = () => !document.hidden && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      axios.post(`${API}/notifications/${n.id}/read`, {}, { headers }).catch(() => {});
    }
    navigate('/account?tab=orders');
  };

  const markAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, read: true })));
    setUnread(0);
    axios.post(`${API}/notifications/read-all`, {}, { headers }).catch(() => {});
  };

  return (
    <DropdownMenu dir="rtl" open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={unread ? `الإشعارات (${unread} غير مقروء)` : 'الإشعارات'}
          className="relative p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-burgundy-500"
        >
          <Bell className="w-6 h-6 text-brand-black" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-burgundy-500 text-white text-xs min-w-5 h-5 px-1 rounded-full flex items-center justify-center font-bold">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="w-[340px] max-w-[calc(100vw-1.5rem)] p-0 font-cairo overflow-hidden rounded-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-tajawal font-bold text-base text-brand-black">الإشعارات</span>
          {unread > 0 && (
            <button type="button" onClick={markAll} className="inline-flex items-center gap-1 text-xs font-semibold text-burgundy-500 hover:underline">
              <CheckCheck className="w-3.5 h-3.5" />
              تحديد الكل كمقروء
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              <Bell className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">لا توجد إشعارات بعد</p>
            <p className="text-xs text-gray-400 mt-1">سنبلغكِ هنا بكل تحديث على طلباتك</p>
          </div>
        ) : (
          <ul className="max-h-[380px] overflow-y-auto divide-y">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className={cn('w-full text-right flex gap-3 px-4 py-3 transition-colors hover:bg-gray-50', !n.read && 'bg-burgundy-500/[0.05]')}
                >
                  <span className="shrink-0 w-10 h-10 rounded-full bg-white ring-1 ring-gray-200 flex items-center justify-center text-lg">{n.emoji}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className={cn('text-sm truncate', n.read ? 'font-semibold text-gray-700' : 'font-bold text-brand-black')}>{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-burgundy-500 shrink-0" />}
                    </span>
                    {n.order_number && <span className="block text-[11px] font-mono text-burgundy-500 mt-0.5" dir="ltr" style={{ textAlign: 'right' }}>{n.order_number}</span>}
                    <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</span>
                    <span className="block text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
